/**
 * ThreadProvider — resolves activeThreadId from (activeStyleId, activeBomId).
 *
 * Phase 2 implementation uses a deterministic local key derived from the IDs.
 * Phase 8 adds useThreadStorageKey() so SimpleLeftPanel can scope its chat
 * history localStorage key to the active style/BOM thread.
 *
 * Depends on: ActiveObjectProvider
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useActiveObject } from "./ActiveObjectProvider.js";

export interface ThreadContextValue {
  activeThreadId: string | null;
}

const ThreadCtx = createContext<ThreadContextValue | null>(null);

export function useThread(): ThreadContextValue {
  const ctx = useContext(ThreadCtx);
  if (!ctx) throw new Error("useThread must be called inside <ThreadProvider>");
  return ctx;
}

/**
 * Returns the localStorage key for storing chat history for the active thread.
 * Falls back to "studio_chat_landing" when no style is active.
 */
export function useThreadStorageKey(): string {
  const { activeThreadId } = useThread();
  return activeThreadId ? `studio_chat:${activeThreadId}` : "studio_chat_landing";
}

export function ThreadProvider({ children }: { children: ReactNode }) {
  const { activeStyleId, activeBomId } = useActiveObject();
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeStyleId) {
      setActiveThreadId(null);
      return;
    }
    const key = activeBomId
      ? `thread:style:${activeStyleId}:bom:${activeBomId}`
      : `thread:style:${activeStyleId}`;
    setActiveThreadId(key);
  }, [activeStyleId, activeBomId]);

  return (
    <ThreadCtx.Provider value={{ activeThreadId }}>
      {children}
    </ThreadCtx.Provider>
  );
}
